import { For, If, onMount, reactive } from 'cdui-js';

import { orignCountryList } from '../../../../stores/bus-config';
import i18n from '../../../../i18n';
import { DiFiInput } from '../../../../components/DiFi/DiFiInput';
import { DiFiNoData } from '../../../../components/DiFi/DiFiNoData';
import { ActionSheetCheckIcon } from '../ActionSheet';
import '../ActionSheet/style.css';
import './style.css';

/** 默认常用：文案 / 区号 / 简称均可；按配置顺序展示 */
export const DEFAULT_COMMON_COUNTRY_KEYS = ['+86', '+852', '+886', '+853'];

export type CountriesProps = {
  value?: string | number;
  current?: string | number;
  change?: (value: string | number, item?: any) => void;
  /** 常用匹配关键字：支持国家文案、区号（+86 / 86）、简称（code） */
  commonKeys?: string[];
  /**
   * 是否在选项后展示区号，如「中国 (86)」。
   * 选手机区号时为 true；选国籍/出生地/居住国等仅国家名时保持 false。
   */
  showDialCode?: boolean;
  /**
   * 自备分组（如 CRS 直接用 /sys/dict/country）。
   * 项上优先读 code，没有再读 value。不传则仍用全局 orignCountryList。
   */
  groups?: Record<string, any[]>;
};

// 防抖函数：优化滚动性能（避免频繁触发）
function debounce(fn, delay = 10) {
  let timer = null;
  return function () {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, arguments);
    }, delay);
  };
}

export const flatCountries = () =>
  Object.values(orignCountryList).flatMap((group: any) => (Array.isArray(group) ? group : []));

const normalizeDial = (v: unknown) =>
  String(v ?? '')
    .replace(/^\+/, '')
    .trim();

/** 国家字典 extra 是不带 + 的区号，如 86；登录/注册 countryCode 统一存 +86 */
export const toDialCode = (extra: unknown) => {
  const digits = normalizeDial(extra);
  return digits ? `+${digits}` : '';
};

/** 国家简称（CN / HK）→ 区号（+86 / +852） */
export const dialFromCountryValue = (code: string | number) => {
  const hit = flatCountries().find((c) => String(c.code ?? c.value ?? '') === String(code));
  return toDialCode(hit?.extra);
};

/** 区号（+86 / 86）→ 国家简称（CN），供 Countries 选中态匹配 */
export const countryValueFromDial = (dial: string | number) => {
  const digits = normalizeDial(dial);
  if (!digits) return '';
  const hit = flatCountries().find((c) => normalizeDial(c.extra) === digits);
  return String(hit?.code ?? hit?.value ?? '');
};

const matchCommonKey = (item: any, key: string) => {
  const k = key.trim();
  if (!k) return false;
  const dialKey = normalizeDial(k);
  if (item.label && String(item.label).includes(k)) return true;
  if (String(item.code ?? item.value ?? '') === k) return true;
  if (dialKey && normalizeDial(item.extra) === dialKey) return true;
  return false;
};

/** 按 commonKeys 顺序从全量数据中匹配常用项，去重 */
export const resolveCommonCountries = (keys: string[] = DEFAULT_COMMON_COUNTRY_KEYS) => {
  const all = flatCountries();
  const result: any[] = [];
  const seen = new Set<string>();
  for (const key of keys) {
    const hit = all.find((item) => matchCommonKey(item, key));
    if (!hit) continue;
    const id = String(hit.value ?? hit.code ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    result.push(hit);
  }
  return result;
};

export const Countries = (props: CountriesProps) => {
  const state = reactive({
    keyword: '',
    searchList: [],
  });

  onMount(() => {
    const stickyLetter = document.getElementById('stickyLetter') as HTMLElement;
    const countryListContainer = document.getElementById('countryListContainer');

    let groupPositions = [];

    function initGroupPositions() {
      groupPositions = [];
      // 先获取滚动容器的视口位置（相对于浏览器视口）
      const containerRect = countryListContainer.getBoundingClientRect();
      // 获取所有字母分组（过滤掉常用区，只处理字母分组）
      const letterGroups = document.querySelectorAll('.letter-group');

      letterGroups.forEach((group: HTMLElement) => {
        const letter = group.dataset.letter;
        // 获取分组的视口位置
        const groupRect = group.getBoundingClientRect();
        // 计算分组相对于滚动容器的内部顶部偏移（关键！）
        const relativeTop = groupRect.top - containerRect.top + countryListContainer.scrollTop;
        // 分组的高度（不变）
        const height = group.offsetHeight;
        // 存储相对于滚动容器的偏移（top/bottom都是容器内的坐标）
        groupPositions.push({
          letter,
          top: relativeTop,
          bottom: relativeTop + height,
        });
      });
    }

    function handleScroll() {
      // 获取滚动容器的滚动距离
      const scrollTop = countryListContainer.scrollTop;
      // 默认隐藏吸顶字母

      let currentLetter = '';

      // 遍历分组位置，判断当前滚动位置属于哪个分组
      for (let i = 0; i < groupPositions.length; i++) {
        const group = groupPositions[i];
        // 条件1：滚动距离 >= 分组顶部（进入该分组）
        // 条件2：滚动距离 < 分组底部（未离开该分组）
        if (scrollTop >= group.top && scrollTop < group.bottom) {
          currentLetter = group.letter;
          break;
        }
      }

      // 如果有当前字母，更新吸顶容器
      if (currentLetter) {
        stickyLetter.textContent = currentLetter;
      }
    }

    setTimeout(() => {
      initGroupPositions();
    }, 200);

    countryListContainer.addEventListener('scroll', debounce(handleScroll));
  });

  const sourceGroups = () =>
    props.groups && Object.keys(props.groups).length > 0 ? props.groups : orignCountryList;

  const sourceFlat = () =>
    Object.values(sourceGroups() || {}).flatMap((group: any) => (Array.isArray(group) ? group : []));

  const itemCode = (item: any) => String(item?.code ?? item?.value ?? '');

  const handleInput = (val: string) => {
    const keyword = (val ?? '').trim();
    state.keyword = keyword;
    if (!keyword) {
      state.searchList = [];
      return;
    }
    const all = props.groups ? sourceFlat() : flatCountries();
    state.searchList = all.filter((c) => (c.label || '').includes(keyword));
  };

  const getSelectedValue = () => props.value ?? props.current;

  const getCommonList = () => {
    const keys = props.commonKeys?.length ? props.commonKeys : DEFAULT_COMMON_COUNTRY_KEYS;
    if (!props.groups) return resolveCommonCountries(keys);
    const all = sourceFlat();
    const result: any[] = [];
    const seen = new Set<string>();
    for (const key of keys) {
      const hit = all.find((item) => matchCommonKey(item, key));
      if (!hit) continue;
      const id = itemCode(hit);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      result.push(hit);
    }
    return result;
  };

  const getItem = (item) => {
    const code = itemCode(item);
    const active = String(getSelectedValue() ?? '') === code;
    const dial = String(item.extra ?? '').trim();
    const label =
      props.showDialCode && dial ? `${item.label} (${dial})` : String(item.label ?? '');
    return (
      <div
        class={`action-sheet-option select-country-item${active ? ' action-sheet-option-active' : ''}`.trim()}
        onclick={() => props.change?.(code, item)}
      >
        <div class="action-sheet-option-label">{label}</div>

        <If when={active}>
          <ActionSheetCheckIcon />
        </If>
      </div>
    );
  };

  return (
    <div class="select-country-panel font-s-S color-D">
      <div class="select-country-search">
        <DiFiInput placeholder={i18n.Common.PleaseEnter} debounceMs={300} maxLength={50} onUpdateValue={handleInput} />
      </div>

      <If when={!!state.keyword}>
        <div class="select-country-list" id="countryListContainer">
          <If when={state.searchList.length}>{state.searchList.map((item) => getItem(item))}</If>
          <If when={!state.searchList.length}>
            <DiFiNoData hasIcon style={{ 'margin-top': '24px' }} text={i18n.Common.NoMatch} />
          </If>
        </div>
      </If>

      <If when={!state.keyword}>
        {sourceGroups() && Object.keys(sourceGroups()).length > 0 ? (
          <div class="select-country-list">
            <div class="select-country-common">
              <For each={getCommonList()}>{(item: any) => getItem(item)}</For>
            </div>

            <div class="select-country-letter flex align-center" id="stickyLetter">
              A
            </div>
            <div id="countryListContainer">
              {Object.keys(sourceGroups()).map((key) => (
                <div class="select-country-group" style={{ position: 'relative', top: '-42px' }}>
                  <div class="select-country-letter flex align-center">{key}</div>
                  <div class="letter-group" data-letter={key}>
                    <For each={sourceGroups()[key]}>{(item: any) => getItem(item)}</For>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <DiFiNoData hasIcon style={{ 'margin-top': '24px' }} text={i18n.Common.NoData} />
        )}
      </If>
    </div>
  );
};
